import { useEffect, useState } from "react";

/** Default quiet period. Long enough to swallow a burst of typing, short enough to feel live. */
export const DEFAULT_DEBOUNCE_MS = 300;

/**
 * Trail `value` by `delayMs` of inactivity — the standard "wait until they stop typing" tool.
 *
 * Use this, NOT `useDeferredValue`, when the value drives a network request. `useDeferredValue`
 * defers *rendering* under concurrent React; it does not batch over time, so a value feeding a
 * query key still changes on essentially every keystroke and still fires a request per character.
 * This hook is the one that reduces request count.
 *
 * The cleanup clearing the previous timer is what makes it a debounce rather than a delay: each
 * new keystroke cancels the pending update, so only the last value in a burst ever lands.
 */
export function useDebouncedValue<T>(
  value: T,
  delayMs: number = DEFAULT_DEBOUNCE_MS
): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(value);
    }, delayMs);
    return () => {
      clearTimeout(timer);
    };
  }, [value, delayMs]);

  return debounced;
}
